// 抽礼物（EP-006-S3 / GDD 01 §②-③）。
//
// 纯函数，rng 必须注入（GDD 01 §2.5 验收前置）。本文件属 domain 层，**不得**直接调用
// Date.now / Math.random（CI 守门 check-no-tick-accumulator）；rolledAt 由调用方注入 `now`。
//
// 流程：选池 → 应用传说保护（tripCount < 10 传说清零 + 回填普通档）→ 抽件数（84/13/3）
//        → 每件独立抽样（受 sameGiftMaxPerTrip 上限约束）→ 返回 giftIds（长度 ≥ 1，设计红线）。
//
// 备注：长旅行倾斜（稀有×1.5 / 传说×2）需要 isLongTrip 上下文，本批次 roll 签名未携带，
//       故长旅行倾斜延后实现；本批次断言套件使用常规旅行，结论不受影响（见 CHANGELOG）。
import type { DestinationId } from '../../save/types';
import type { DropConfig } from './dropConfig';
import { FALLBACK_POOL } from './dropConfig';
import { GIFT_BY_ID } from '../gifts/giftDef';
import { weightedPick } from '../../core/weightedPick';

export interface RollResult {
  giftIds: string[];
  rolledAt: number;
}

// 传说保护（GDD 01 §2.3）：保护期内传说权重清零，按同池普通档现有权重比例回填普通档，
// 少见/稀有档不变。返回新的权重表，不修改入参。
function applyLegendaryProtection(
  pool: Record<string, number>,
  tripCount: number,
  legendaryProtectionTrips: number,
): Record<string, number> {
  if (tripCount >= legendaryProtectionTrips) {
    return { ...pool };
  }

  let legendaryTotal = 0;
  let normalTotal = 0;
  for (const [id, w] of Object.entries(pool)) {
    const rarity = GIFT_BY_ID[id]?.rarity;
    if (rarity === 'L') legendaryTotal += w;
    else if (rarity === 'N') normalTotal += w;
  }
  if (legendaryTotal <= 0 || normalTotal <= 0) {
    // 无传说权重可清，或无可回填的普通档：直接清零传说档即可
    return Object.fromEntries(
      Object.entries(pool).map(([id, w]) => [id, GIFT_BY_ID[id]?.rarity === 'L' ? 0 : w]),
    );
  }

  const factor = 1 + legendaryTotal / normalTotal;
  const out: Record<string, number> = {};
  for (const [id, w] of Object.entries(pool)) {
    const rarity = GIFT_BY_ID[id]?.rarity;
    if (rarity === 'L') out[id] = 0;
    else if (rarity === 'N') out[id] = w * factor;
    else out[id] = w;
  }
  return out;
}

function pickCount(config: DropConfig, rng: () => number): number {
  const ks = config.countWeights.map((c) => c.k);
  const ws = config.countWeights.map((c) => c.w);
  return weightedPick(ks, ws, rng);
}

export function roll(
  config: DropConfig,
  destination: DestinationId,
  tripCount: number,
  rng: () => number,
  now: number,
): RollResult {
  const basePool = config.pools[destination] ?? FALLBACK_POOL;
  const protectedPool = applyLegendaryProtection(basePool, tripCount, config.tuning.legendaryProtectionTrips);

  const ids = Object.keys(protectedPool).filter((id) => (protectedPool[id] ?? 0) > 0);
  const weights = ids.map((id) => protectedPool[id] as number);
  if (ids.length === 0) {
    throw new Error(`roll: 目的地 ${destination} 池为空，且兜底池不可用`);
  }

  const count = pickCount(config, rng);
  const seen = new Map<string, number>();
  const giftIds: string[] = [];
  const maxPer = config.tuning.sameGiftMaxPerTrip;

  for (let i = 0; i < count; i++) {
    let chosen = '';
    // 同物上限：达到上限则重抽（最多 10 次），仍超限则接受（优雅降级，绝不返回空数组）。
    for (let attempt = 0; attempt < 10; attempt++) {
      chosen = weightedPick(ids, weights, rng);
      const c = seen.get(chosen) ?? 0;
      if (c + 1 <= maxPer) break;
    }
    seen.set(chosen, (seen.get(chosen) ?? 0) + 1);
    giftIds.push(chosen);
  }

  if (giftIds.length === 0) {
    // 设计红线（GDD 01 A1）：永不空手。理论上不可达，作为最后防线再抽一次。
    giftIds.push(weightedPick(ids, weights, rng));
  }

  return { giftIds, rolledAt: now };
}
